/**
 * Agents v3 — the builder's scripted intake (`/agents/new`).
 *
 * Before the builder (the sidecar) is asked for a proposal, the chat walks
 * the user through a fixed set of questions so the agent is never designed
 * on guesses: the goal, the data it reads, the KPIs it judges by, when it
 * should raise a flag, how often it runs, what it delivers, and any extras.
 * The answers become one "Intake brief" user message — the first (and often
 * only) turn the sidecar sees before it proposes.
 *
 * Pure functions here; the component owns the state.
 */
import type { AgentCadence, AgentOutputMode, BuilderSession } from '../../../lib/api/client-reports/client-agents-api';
import { composeCronSchedule, describeSchedule } from '../../client-reports/agents/agent-helpers';

export type IntakeStepId = 'goal' | 'sources' | 'kpis' | 'trigger' | 'frequency' | 'output' | 'extras';

export interface IntakeOption {
  /** What is sent/stored. */
  value: string;
  /** What the chip shows. */
  label: string;
  /** Optional second line under the label. */
  hint?: string;
}

export interface IntakeStep {
  id: IntakeStepId;
  /** The assistant bubble. */
  question: string;
  /** A muted line under the question (why we ask / what to do if unsure). */
  hint?: string;
  /** Free text always works; `choice` steps also offer chips. */
  kind: 'text' | 'choice' | 'schedule';
  /** choice: several chips may be picked (with a Continue button). */
  multi?: boolean;
  /** text: chips that PREFILL the input instead of answering. */
  examples?: string[];
  options?: IntakeOption[];
  /** The input placeholder while this step is active. */
  placeholder: string;
  /** Optional steps can be passed with one click. */
  optional?: boolean;
  /** The label of the "pass" chip for optional steps. */
  skipLabel?: string;
}

export interface IntakeAnswers {
  goal?: string;
  sources?: string[];
  kpis?: string[];
  trigger?: string;
  /**
   * Composed cron + timezone (the picker's format), or the user's own words
   * when they typed the frequency — the builder turns those into a schedule.
   */
  frequency?: { cron: string; timezone: string; text?: string };
  output?: AgentOutputMode | string;
  extras?: string;
}

/** The steps in order — the required ones are what the builder must know. */
export const INTAKE_ORDER: IntakeStepId[] = ['goal', 'sources', 'kpis', 'trigger', 'frequency', 'output', 'extras'];

export const REQUIRED_STEPS: IntakeStepId[] = ['goal', 'sources', 'kpis', 'trigger', 'frequency', 'output'];

/** One- or two-word labels for the wizard's stepper row. */
export const STEP_SHORT: Record<IntakeStepId, string> = {
  goal: 'Goal',
  sources: 'Data',
  kpis: 'KPIs',
  trigger: 'Flag when',
  frequency: 'How often',
  output: 'Delivers',
  extras: 'Extras',
};

/** Fuller labels for the review card's "what you told the builder" list. */
export const STEP_LABEL: Record<IntakeStepId, string> = {
  goal: 'Goal',
  sources: 'Data it reads',
  kpis: 'KPIs it judges by',
  trigger: 'When to raise a flag',
  frequency: 'How often',
  output: 'What it delivers',
  extras: 'Extras',
};

export const OUTPUT_OPTIONS: IntakeOption[] = [
  { value: 'findings', label: 'A findings brief', hint: 'Bullets + a one-page HTML for your team' },
  { value: 'client_report', label: 'A client-facing report', hint: 'Goes through your review queue first' },
  { value: 'workbook', label: 'An Excel workbook', hint: 'The numbers, laid out' },
];

const PLATFORM_LABEL: Record<string, string> = {
  facebook: 'Meta',
  google_ads: 'Google Ads',
  ga4: 'Google Analytics 4',
  gsc: 'Search Console',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  shopify: 'Shopify',
  hubspot: 'HubSpot',
  intercom: 'Intercom',
  bing_ads: 'Microsoft Ads',
  microsoft_ads: 'Microsoft Ads',
};

export const platformLabel = (p: string): string => PLATFORM_LABEL[p] || p.replace(/_/g, ' ');

const METRIC_LABEL: Record<string, string> = {
  cpa: 'CPA',
  roas: 'ROAS',
  aov: 'AOV',
  ctr: 'CTR',
  cpc: 'CPC',
  cpm: 'CPM',
  cvr: 'Conversion rate',
  spend: 'Spend',
  revenue: 'Revenue',
  orders: 'Orders',
  conversions: 'Conversions',
  impressions: 'Impressions',
  clicks: 'Clicks',
  sessions: 'Sessions',
  leads: 'Leads',
};

export const metricLabel = (m: string): string =>
  METRIC_LABEL[m.toLowerCase()] || m.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** The metrics offered when a brand has no KPI targets yet. */
const COMMON_METRICS = ['roas', 'cpa', 'spend', 'conversions', 'revenue', 'ctr'];

export const ALL_SOURCES = '__all__';

/**
 * The translation key for each English string of the intake script. The
 * script stays English (its values make up the brief the builder reads);
 * the wizard translates what it shows through `intakeText`.
 */
export const INTAKE_TEXT_KEYS: Record<string, string> = {
  "Goal": "AgentIntake_StepGoal",
  "Data": "AgentIntake_StepData",
  "Flag when": "AgentIntake_StepFlagWhen",
  "How often": "AgentIntake_StepHowOften",
  "Delivers": "AgentIntake_StepDelivers",
  "Extras": "AgentIntake_StepExtras",
  "A findings brief": "AgentIntake_OutputFindings",
  "Bullets + a one-page HTML for your team": "AgentIntake_OutputFindingsHint",
  "A client-facing report": "AgentIntake_OutputClientReport",
  "Goes through your review queue first": "AgentIntake_OutputClientReportHint",
  "An Excel workbook": "AgentIntake_OutputWorkbook",
  "The numbers, laid out": "AgentIntake_OutputWorkbookHint",
  "Conversion rate": "AgentIntake_MetricConversionRate",
  "Spend": "AgentIntake_MetricSpend",
  "Revenue": "AgentIntake_MetricRevenue",
  "Orders": "AgentIntake_MetricOrders",
  "Conversions": "AgentIntake_MetricConversions",
  "Impressions": "AgentIntake_MetricImpressions",
  "Clicks": "AgentIntake_MetricClicks",
  "Sessions": "AgentIntake_MetricSessions",
  "Leads": "AgentIntake_MetricLeads",
  "All connected platforms": "AgentIntake_AllSources",
  "What should this agent do for you?": "AgentIntake_GoalQuestion",
  "Say it the way you would brief a colleague — one or two sentences; the details come next.": "AgentIntake_GoalHint",
  "Flag campaigns that miss their CPA or ROAS target": "AgentIntake_GoalExample1",
  "Spot wasted ad spend — clicks that never convert": "AgentIntake_GoalExample2",
  "Summarise last week's results against our KPI targets": "AgentIntake_GoalExample3",
  "Warn me when tracking or conversion data looks broken": "AgentIntake_GoalExample4",
  "For example: tell me which campaigns are burning budget without conversions…": "AgentIntake_GoalPlaceholder",
  "Which data should it look at?": "AgentIntake_SourcesQuestion",
  "Only the platforms connected to this brand can be read.": "AgentIntake_SourcesHint",
  "Nothing is connected to this brand yet — name what you will connect; the agent has no data until then.": "AgentIntake_SourcesHintNone",
  "Or type the platforms…": "AgentIntake_SourcesPlaceholder",
  "Type the platforms you will connect…": "AgentIntake_SourcesPlaceholderNone",
  "Which KPIs should it judge results by?": "AgentIntake_KpisQuestion",
  "These are the KPI targets set for this brand — the agent compares against them.": "AgentIntake_KpisHint",
  "No KPI targets are set for this brand yet. The agent can still watch these metrics; set targets later under Reporting → KPI targets.": "AgentIntake_KpisHintNone",
  "Or type the metrics…": "AgentIntake_KpisPlaceholder",
  "When should it raise a flag?": "AgentIntake_TriggerQuestion",
  "What counts as a problem — a missed target, a big swing, or always report.": "AgentIntake_TriggerHint",
  "Whenever a KPI misses its target": "AgentIntake_TriggerMissed",
  "A swing of more than 20% vs the previous period": "AgentIntake_TriggerSwing",
  "Only above a spend threshold": "AgentIntake_TriggerSpend",
  "Every run — always summarise": "AgentIntake_TriggerAlways",
  "Or describe it in your own words…": "AgentIntake_TriggerPlaceholder",
  "How often, and when, should it run?": "AgentIntake_FrequencyQuestion",
  "Pick a quick option or set the exact day and time.": "AgentIntake_FrequencyHint",
  "Or type it, e.g. \"every Friday at 16:00\"…": "AgentIntake_FrequencyPlaceholder",
  "What should it deliver?": "AgentIntake_OutputQuestion",
  "Or describe what you want to receive…": "AgentIntake_OutputPlaceholder",
  "Nothing else — build it": "AgentIntake_ExtrasSkip",
  "Anything it should know, include or avoid? (optional)": "AgentIntake_ExtrasQuestion",
  "Tone, must-include numbers, campaigns to ignore, thresholds, language.": "AgentIntake_ExtrasHint",
  "Optional — for example: ignore the brand campaigns, write in Dutch…": "AgentIntake_ExtrasPlaceholder",
  "Every Monday at 07:00": "AgentIntake_PresetWeekly",
  "Every day at 08:00": "AgentIntake_PresetDaily",
  "1st of every month at 07:00": "AgentIntake_PresetMonthly",
};

/** The shown text for an intake string: translated when it is known. */
export function intakeText(t: (key: string, defaultValue: string) => string, text: string): string {
  const key = INTAKE_TEXT_KEYS[text];
  return key ? t(key, text) : text;
}

/** The intake script for a brand, built from the session context. */
export function intakeSteps(context: BuilderSession['context'] | undefined): IntakeStep[] {
  const platforms = Array.isArray(context?.connected_platforms)
    ? (context!.connected_platforms as string[]).filter((p) => typeof p === 'string' && p !== '')
    : [];
  const hasTargets = !!context?.has_kpi_targets;
  const metrics = Array.isArray(context?.kpi_metrics)
    ? (context!.kpi_metrics as string[]).filter((m) => typeof m === 'string' && m !== '')
    : [];
  const metricOptions = (metrics.length > 0 ? metrics : COMMON_METRICS).map((m) => ({
    value: m,
    label: metricLabel(m),
  }));

  return [
    {
      id: 'goal',
      kind: 'text',
      question: 'What should this agent do for you?',
      hint: 'Say it the way you would brief a colleague — one or two sentences; the details come next.',
      examples: [
        'Flag campaigns that miss their CPA or ROAS target',
        'Spot wasted ad spend — clicks that never convert',
        "Summarise last week's results against our KPI targets",
        'Warn me when tracking or conversion data looks broken',
      ],
      placeholder: 'For example: tell me which campaigns are burning budget without conversions…',
    },
    {
      id: 'sources',
      kind: 'choice',
      multi: true,
      question: 'Which data should it look at?',
      hint:
        platforms.length > 0
          ? 'Only the platforms connected to this brand can be read.'
          : 'Nothing is connected to this brand yet — name what you will connect; the agent has no data until then.',
      options: [
        ...(platforms.length > 1 ? [{ value: ALL_SOURCES, label: 'All connected platforms' }] : []),
        ...platforms.map((p) => ({ value: p, label: platformLabel(p) })),
      ],
      placeholder: platforms.length > 0 ? 'Or type the platforms…' : 'Type the platforms you will connect…',
    },
    {
      id: 'kpis',
      kind: 'choice',
      multi: true,
      question: 'Which KPIs should it judge results by?',
      hint: hasTargets
        ? 'These are the KPI targets set for this brand — the agent compares against them.'
        : 'No KPI targets are set for this brand yet. The agent can still watch these metrics; set targets later under Reporting → KPI targets.',
      options: metricOptions,
      placeholder: 'Or type the metrics…',
    },
    {
      id: 'trigger',
      kind: 'choice',
      question: 'When should it raise a flag?',
      hint: 'What counts as a problem — a missed target, a big swing, or always report.',
      options: [
        { value: 'Whenever a KPI misses its target', label: 'Whenever a KPI misses its target' },
        {
          value: 'When a KPI moves more than 20% versus the previous period',
          label: 'A swing of more than 20% vs the previous period',
        },
        {
          value: 'Only for campaigns or ad sets above a spend threshold I will give in the notes',
          label: 'Only above a spend threshold',
        },
        { value: 'Every run — always give me the full summary', label: 'Every run — always summarise' },
      ],
      placeholder: 'Or describe it in your own words…',
    },
    {
      id: 'frequency',
      kind: 'schedule',
      question: 'How often, and when, should it run?',
      hint: 'Pick a quick option or set the exact day and time.',
      placeholder: 'Or type it, e.g. "every Friday at 16:00"…',
    },
    {
      id: 'output',
      kind: 'choice',
      question: 'What should it deliver?',
      options: OUTPUT_OPTIONS,
      placeholder: 'Or describe what you want to receive…',
    },
    {
      id: 'extras',
      kind: 'text',
      optional: true,
      skipLabel: 'Nothing else — build it',
      question: 'Anything it should know, include or avoid? (optional)',
      hint: 'Tone, must-include numbers, campaigns to ignore, thresholds, language.',
      placeholder: 'Optional — for example: ignore the brand campaigns, write in Dutch…',
    },
  ];
}

/** Quick schedule chips for the frequency step. */
export function frequencyPresets(cadences: AgentCadence[] | undefined): Array<{ label: string; cron: string }> {
  const allowed = Array.isArray(cadences) && cadences.length > 0 ? cadences : ['daily', 'weekly', 'monthly'];
  const out: Array<{ label: string; cron: string }> = [];
  if (allowed.includes('weekly')) out.push({ label: 'Every Monday at 07:00', cron: composeCronSchedule({ cadence: 'weekly', day: 1, hour: 7, minute: 0 }) });
  if (allowed.includes('daily')) out.push({ label: 'Every day at 08:00', cron: composeCronSchedule({ cadence: 'daily', day: 0, hour: 8, minute: 0 }) });
  if (allowed.includes('monthly')) out.push({ label: '1st of every month at 07:00', cron: composeCronSchedule({ cadence: 'monthly', day: 1, hour: 7, minute: 0 }) });
  return out;
}

/** The first unanswered step in order, or null when the intake is complete. */
export function nextIntakeStep(answers: IntakeAnswers, steps: IntakeStep[]): IntakeStep | null {
  for (const step of steps) {
    if (!isAnswered(step.id, answers)) return step;
  }
  return null;
}

export function isAnswered(id: IntakeStepId, answers: IntakeAnswers): boolean {
  switch (id) {
    case 'goal':
      return !!answers.goal?.trim();
    case 'sources':
      return Array.isArray(answers.sources) && answers.sources.length > 0;
    case 'kpis':
      return Array.isArray(answers.kpis) && answers.kpis.length > 0;
    case 'trigger':
      return !!answers.trigger?.trim();
    case 'frequency':
      return !!(answers.frequency?.cron || answers.frequency?.text?.trim());
    case 'output':
      return !!answers.output;
    case 'extras':
      return answers.extras !== undefined;
    default:
      return false;
  }
}

/** Every required step answered — the brief can be composed. */
export function intakeComplete(answers: IntakeAnswers): boolean {
  return REQUIRED_STEPS.every((id) => isAnswered(id, answers));
}

/** The user bubble shown for an answer. */
export function answerSummary(id: IntakeStepId, answers: IntakeAnswers): string {
  switch (id) {
    case 'goal':
      return answers.goal ?? '';
    case 'sources':
      return (answers.sources ?? []).map((s) => (s === ALL_SOURCES ? 'All connected platforms' : platformLabel(s))).join(', ');
    case 'kpis':
      return (answers.kpis ?? []).map(metricLabel).join(', ');
    case 'trigger':
      return answers.trigger ?? '';
    case 'frequency':
      if (!answers.frequency) return '';
      return answers.frequency.cron
        ? describeSchedule(answers.frequency.cron, answers.frequency.timezone)
        : answers.frequency.text ?? '';
    case 'output':
      return OUTPUT_OPTIONS.find((o) => o.value === answers.output)?.label ?? String(answers.output ?? '');
    case 'extras':
      return answers.extras?.trim() ? answers.extras : 'Nothing else.';
    default:
      return '';
  }
}

/**
 * The one message the sidecar gets: every answer on its own line, in the
 * order the builder needs them. Starts with "Intake brief" — the sidecar's
 * prompt treats that as complete answers and proposes right away.
 */
export function composeIntakeBrief(answers: IntakeAnswers, brandName: string): string {
  const lines = [
    `Intake brief for ${brandName || 'this brand'}:`,
    `- Goal: ${answers.goal?.trim() ?? ''}`,
    `- Data sources: ${answerSummary('sources', answers)}`,
    `- KPIs to judge by: ${answerSummary('kpis', answers)}`,
    `- Raise a flag when: ${answers.trigger?.trim() ?? ''}`,
    `- Frequency: ${answerSummary('frequency', answers)}${
      answers.frequency?.cron ? ` (cron ${answers.frequency.cron}, ${answers.frequency.timezone})` : ''
    }`,
    `- Output: ${answerSummary('output', answers)}${
      OUTPUT_OPTIONS.some((o) => o.value === answers.output) ? ` (${answers.output})` : ''
    }`,
    `- Notes: ${answers.extras?.trim() || 'none'}`,
    '',
    'Everything above is decided — please propose the complete agent now.',
  ];
  return lines.join('\n');
}
